/**
 * Tabs Component
 *
 * Modern, accessible tabs component for organizing content.
 *
 * Usage:
 * <Tabs defaultTab="overview">
 *   <TabList>
 *     <Tab id="overview">Overview</Tab>
 *     <Tab id="endpoints">Endpoints</Tab>
 *   </TabList>
 *   <TabPanel id="overview">Overview content</TabPanel>
 *   <TabPanel id="endpoints">Endpoints content</TabPanel>
 * </Tabs>
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

interface TabsContextType {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

const useTabs = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
};

interface TabsProps {
  children: ReactNode;
  defaultTab: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  onChange?: (tabId: string) => void;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  children,
  defaultTab,
  activeTab: controlledActiveTab,
  onTabChange,
  onChange,
  className = '',
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab);

  // Use controlled activeTab if provided, otherwise use internal state
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  const handleTabChange = (id: string) => {
    // Update internal state if not controlled
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(id);
    }
    // Call callbacks
    onTabChange?.(id);
    onChange?.(id);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange }}>
      <div className={`tabs-container ${className}`}>{children}</div>
    </TabsContext.Provider>
  );
};

interface TabListProps {
  children: ReactNode;
  className?: string;
}

export const TabList: React.FC<TabListProps> = ({ children, className = '' }) => {
  return (
    <div
      role="tablist"
      className={`flex border-b border-slate-200 dark:border-slate-800 gap-1 ${className}`}
      style={{ marginBottom: 'var(--space-6)' }}
    >
      {children}
    </div>
  );
};

interface TabProps {
  id: string;
  children: ReactNode;
  disabled?: boolean;
  icon?: ReactNode;
  badge?: string | number;
  className?: string;
}

export const Tab: React.FC<TabProps> = ({
  id,
  children,
  disabled = false,
  icon,
  badge,
  className = '',
}) => {
  const { activeTab, setActiveTab } = useTabs();
  const isActive = activeTab === id;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      aria-controls={`panel-${id}`}
      id={`tab-${id}`}
      disabled={disabled}
      onClick={() => !disabled && setActiveTab(id)}
      className={`
        relative px-4 py-3 font-medium text-sm transition-all
        border-b-2 -mb-px
        ${
          isActive
            ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-700'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-medium)',
      }}
    >
      <span className="flex items-center gap-2">
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span>{children}</span>
        {badge !== undefined && (
          <span
            className={`
              inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full
              ${
                isActive
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
              }
            `}
          >
            {badge}
          </span>
        )}
      </span>
    </button>
  );
};

interface TabPanelProps {
  id: string;
  children: ReactNode;
  className?: string;
}

export const TabPanel: React.FC<TabPanelProps> = ({ id, children, className = '' }) => {
  const { activeTab } = useTabs();
  const isActive = activeTab === id;

  if (!isActive) return null;

  return (
    <div
      role="tabpanel"
      id={`panel-${id}`}
      aria-labelledby={`tab-${id}`}
      className={`tab-panel-content ${className}`}
    >
      {children}
    </div>
  );
};
